import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useRef } from "react";

interface CommentsScrollContextValue {
  cancelScrollToComment: (commentId: number) => void;
  registerCommentScrollTarget: (
    commentId: number,
    element: HTMLElement | null,
  ) => void;
  registerSectionScrollTarget: (element: HTMLElement | null) => void;
  requestScrollToComment: (commentId: number) => void;
  scrollToComposer: (element: HTMLElement | null) => void;
  scrollToSection: () => void;
}

const CommentsScrollContext = createContext<CommentsScrollContextValue | null>(
  null,
);
CommentsScrollContext.displayName = "CommentsScrollContext";

interface CommentsScrollProviderProps {
  children: ReactNode;
  isAutoScrollEnabled: boolean;
}

const scrollToElement = (
  element: HTMLElement,
  options: ScrollIntoViewOptions,
) => {
  requestAnimationFrame(() => {
    element.scrollIntoView(options);
  });
};

export const CommentsScrollProvider = ({
  children,
  isAutoScrollEnabled,
}: CommentsScrollProviderProps) => {
  const commentScrollTargets = useRef(new Map<number, HTMLElement>());
  const pendingCommentId = useRef<number | null>(null);
  const sectionScrollTarget = useRef<HTMLElement | null>(null);

  const requestScrollToComment = useCallback(
    (commentId: number) => {
      if (!isAutoScrollEnabled) return;
      const element = commentScrollTargets.current.get(commentId);
      if (element) {
        scrollToElement(element, { block: "center", behavior: "instant" });
      } else {
        pendingCommentId.current = commentId;
      }
    },
    [isAutoScrollEnabled],
  );

  const registerCommentScrollTarget = useCallback(
    (commentId: number, element: HTMLElement | null) => {
      if (!element) {
        commentScrollTargets.current.delete(commentId);
        return;
      }
      commentScrollTargets.current.set(commentId, element);
      if (pendingCommentId.current === commentId) {
        pendingCommentId.current = null;
        scrollToElement(element, { block: "center", behavior: "instant" });
      }
    },
    [],
  );

  const cancelScrollToComment = useCallback((commentId: number) => {
    if (pendingCommentId.current === commentId) {
      pendingCommentId.current = null;
    }
  }, []);

  const registerSectionScrollTarget = useCallback(
    (element: HTMLElement | null) => {
      sectionScrollTarget.current = element;
    },
    [],
  );

  const scrollToSection = useCallback(() => {
    if (!isAutoScrollEnabled || !sectionScrollTarget.current) return;
    scrollToElement(sectionScrollTarget.current, {
      block: "start",
      behavior: "smooth",
    });
  }, [isAutoScrollEnabled]);

  const scrollToComposer = useCallback(
    (element: HTMLElement | null) => {
      if (!isAutoScrollEnabled || !element) return;
      scrollToElement(element, { block: "center", behavior: "smooth" });
    },
    [isAutoScrollEnabled],
  );

  const value = useMemo<CommentsScrollContextValue>(
    () => ({
      cancelScrollToComment,
      registerCommentScrollTarget,
      registerSectionScrollTarget,
      requestScrollToComment,
      scrollToComposer,
      scrollToSection,
    }),
    [
      cancelScrollToComment,
      registerCommentScrollTarget,
      registerSectionScrollTarget,
      requestScrollToComment,
      scrollToComposer,
      scrollToSection,
    ],
  );

  return (
    <CommentsScrollContext.Provider value={value}>
      {children}
    </CommentsScrollContext.Provider>
  );
};

export const useCommentsScroll = (): CommentsScrollContextValue => {
  const value = useContext(CommentsScrollContext);
  if (!value) {
    throw new Error("useCommentsScroll must be used inside Comments.Provider.");
  }
  return value;
};

export const useCommentScrollTarget = (commentId: number) => {
  const { registerCommentScrollTarget } = useCommentsScroll();
  return useCallback(
    (element: HTMLElement | null) => {
      registerCommentScrollTarget(commentId, element);
    },
    [registerCommentScrollTarget, commentId],
  );
};
